import * as React from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@lightspeed/flame/Button';
import { IconCheckmark } from '@lightspeed/flame/Icon/Checkmark';
import { IconDanger } from '@lightspeed/flame/Icon/Danger';
import { IconDesktop } from '@lightspeed/flame/Icon/Desktop';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import semver from 'semver';

import { createReactRenderer } from '../helpers/react-renderer';
import store from 'pages/store';
import { socketClient } from 'pages/views/devices/socketClient';
import { CFD_MESSAGES } from 'pages/views/devices/socketClient/types';
import { getOptions } from 'pages/views/devices/utils/options';

import ExistingCustomerSelection from './ExistingCustomerSelection';
import { CfdCustomerDataInputs } from './types';
import { REGISTER_CUSTOMER_SAVED } from './utils';

export type CustomerInfoSubmissionStage = 'HAS_DUPLICATES' | 'SAVED';

const RegisterCFDCustomer: React.FC = () => {
  const {
    selectedDisplay,
    version,
    connection: { browserCFD },
  } = useSelector((state) => state.hub);
  const customerInfoStatus = window.sessionStorage.getItem(
    REGISTER_CUSTOMER_SAVED
  ) as CustomerInfoSubmissionStage;

  const [isLoading, setIsLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [existingCustomers, setExistingCustomers] = React.useState<Customer[]>([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [customerInput, setCustomerInput] = React.useState<CfdCustomerDataInputs | undefined>(
    undefined
  );
  const editCustomerButton = React.useRef<Element | null>(null);
  const onCustomerSelectionFunction = React.useRef<
    CustomerSelectionProp['onSelection'] | undefined
  >(undefined);

  const hasModule = window.lsCloud.Modules.hasModule('register.cfd.graahak');
  const isHubCfd = browserCFD !== 'Connected' && version && !!Object.keys(selectedDisplay).length;
  const browserCfd = browserCFD === 'Connected';
  const isCfdConnected = browserCfd || isHubCfd;
  const isHubVersionCompatible = isHubCfd
    ? semver.satisfies(version, `>= 1.7.0-beta.1`, { includePrerelease: true })
    : false;

  const actionRequired = customerInfoStatus === 'HAS_DUPLICATES' && existingCustomers.length > 0;
  const handleClick = () => {
    if (actionRequired) {
      setIsModalOpen(true);
    } else {
      setIsLoading(true);
      socketClient.send(CFD_MESSAGES.CUSTOMER_START);
    }
  };

  React.useEffect(() => {
    // Enable the component state to be accessed by the socket client
    setIsVisible(getOptions().isRegisterCustomerVisible);
    window.cancelRegisterCustomer = (isLoading) => {
      setIsLoading(!!isLoading);
    };

    window.cfdSetExistingCustomers = (customerList, onSelection, customerInputs) => {
      if (window.lsCloud.Modules.hasModule('register.cfd.info_confirmation') && customerInputs) {
        setCustomerInput(customerInputs);
      }
      onCustomerSelectionFunction.current = async (id?: string, skipUpdate?: boolean) => {
        setIsLoading(true);
        await onSelection(id, skipUpdate);
      };
      setExistingCustomers(customerList);
    };
    // Do a cleanup when the component unmounts
    return () => window.sessionStorage.removeItem(REGISTER_CUSTOMER_SAVED);
  }, []);

  React.useEffect(() => {
    if (!isCfdConnected || actionRequired) {
      setIsLoading(false);
    }
    editCustomerButton.current = document.querySelector('#customerEditButton');
  }, [isCfdConnected, actionRequired]);

  let content;

  if (!browserCfd && !isHubVersionCompatible) {
    content =
      'To allow customers to update their information on the CFD, the Lightspeed Hub version needs to be 1.7.0 or above';
  }

  if (isLoading) {
    content = 'Customer is entering information, page will reload when complete';
  } else {
    content = 'Ask customer to enter contact information on CFD';

    if (customerInfoStatus === 'SAVED') content = 'Customer information has been saved';

    if (editCustomerButton.current === null)
      content = 'Ask customer to enter contact information on CFD';

    if (actionRequired) content = 'Action Required';
  }

  const renderIcons = () => {
    switch (customerInfoStatus) {
      case 'SAVED': {
        return editCustomerButton.current === null ? <IconDesktop /> : <IconCheckmark />;
      }
      case 'HAS_DUPLICATES': {
        return existingCustomers.length > 0 ? (
          <IconDanger data-test="open-customer-list" />
        ) : (
          <IconDesktop />
        );
      }
      default:
        return <IconDesktop data-test="cfd-ask-customer-info" />;
    }
  };

  return hasModule && isVisible ? (
    <>
      {isCfdConnected && (
        <Tooltip
          key={content}
          placement="bottom"
          // @ts-ignore
          content={<div style={{ maxWidth: '360px' }}>{content}</div>}
        >
          <span style={{ position: 'relative', top: '2px' }}>
            <Button
              minHeight="34px"
              loading={isLoading}
              disabled={!browserCfd && !isHubVersionCompatible}
              onClick={handleClick}
              data-test="register-cfd-customer-button"
            >
              {renderIcons()}
            </Button>
          </span>
        </Tooltip>
      )}
      {onCustomerSelectionFunction.current !== undefined && (
        <ExistingCustomerSelection
          customerInput={customerInput}
          key={JSON.stringify(existingCustomers)}
          customers={existingCustomers}
          onSelection={onCustomerSelectionFunction.current}
          isOpen={isModalOpen}
          modalStateToggle={(state: boolean) => setIsModalOpen(state)}
        />
      )}
    </>
  ) : null;
};

const RenderedComponent = createReactRenderer({
  component: RegisterCFDCustomer,
  selector: '[data-react-root="RegisterCFDCustomer"]',
  store,
});

export default RegisterCFDCustomer;

history.onChangeState(
  [{ pathname: '/register.php', form_name: undefined }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
